<script setup lang="ts">
/**
 * LeadCaptureForm — Public Inbound Web Form Ingestion
 *
 * Implements anti-spam honeypot field (_gotcha) and authenticates
 * via embedKey (X-Embed-Key header) to POST /leads/ingest.
 *
 * @see docs/ACUMATICA-INTEGRATION-2.md Section 6
 */
import { ref } from 'vue'
import { BaseButton } from '@/components'
import { leadService } from '../api/service'
import type { LeadIngestDto } from '../api/types'

const props = defineProps<{
    embedKey?: string
}>()

const form = ref<LeadIngestDto>({
    name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    description: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    _gotcha: '', // Hidden honeypot field
})

const isSubmitting = ref(false)
const isSubmitted = ref(false)
const errorMessage = ref<string | null>(null)

async function handleSubmit() {
    isSubmitting.value = true
    errorMessage.value = null

    try {
        await leadService.ingest(form.value, props.embedKey)
        isSubmitted.value = true
    } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'An error occurred while submitting. Please try again.'
        errorMessage.value = message
    } finally {
        isSubmitting.value = false
    }
}
</script>

<template>
    <div class="lead-capture-wrapper">
        <div v-if="isSubmitted" class="lead-capture-success">
            <div class="success-icon">✓</div>
            <h3>Thank You!</h3>
            <p>Your inquiry has been received. A commercial specialist will contact you shortly.</p>
        </div>

        <form v-else class="lead-capture-form" @submit.prevent="handleSubmit">
            <div v-if="errorMessage" class="error-banner">
                {{ errorMessage }}
            </div>

            <!-- Anti-Spam Honeypot Field — hidden visually from human visitors -->
            <div class="honeypot" aria-hidden="true">
                <label for="_gotcha">Leave this field blank</label>
                <input
                    id="_gotcha"
                    v-model="form._gotcha"
                    type="text"
                    name="_gotcha"
                    tabindex="-1"
                    autocomplete="off"
                />
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label class="form-label" for="lead-capture-name">Full Name <span class="required">*</span></label>
                    <input
                        id="lead-capture-name"
                        v-model="form.name"
                        type="text"
                        required
                        class="form-input"
                        placeholder="Sarah Connor"
                    />
                </div>
                <div class="form-group">
                    <label class="form-label" for="lead-capture-email">Work Email <span class="required">*</span></label>
                    <input
                        id="lead-capture-email"
                        v-model="form.email"
                        type="email"
                        required
                        class="form-input"
                        placeholder="sarah@company.com"
                    />
                </div>
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label class="form-label" for="lead-capture-phone">Phone Number</label>
                    <input
                        id="lead-capture-phone"
                        v-model="form.phone"
                        type="tel"
                        class="form-input"
                        placeholder="+1 (555) 0144"
                    />
                </div>
                <div class="form-group">
                    <label class="form-label" for="lead-capture-company">Company / Property</label>
                    <input
                        id="lead-capture-company"
                        v-model="form.company"
                        type="text"
                        class="form-input"
                        placeholder="Cyberdyne Systems"
                    />
                </div>
            </div>

            <div class="form-group">
                <label class="form-label" for="lead-capture-title">Inquiry Title / Subject</label>
                <input
                    id="lead-capture-title"
                    v-model="form.title"
                    type="text"
                    class="form-input"
                    placeholder="Roof Inspection Estimate - Warehouse B"
                />
            </div>

            <div class="form-group">
                <label class="form-label" for="lead-capture-desc">How can we help?</label>
                <textarea
                    id="lead-capture-desc"
                    v-model="form.description"
                    rows="3"
                    class="form-input form-textarea"
                    placeholder="Provide details regarding your property, recent storm damage, or quote needs..."
                />
            </div>

            <div class="form-actions">
                <BaseButton
                    type="submit"
                    variant="primary"
                    :loading="isSubmitting"
                >
                    Request Consultation
                </BaseButton>
            </div>
        </form>
    </div>
</template>

<style scoped>
.lead-capture-wrapper {
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: 1.5rem;
}

.lead-capture-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.lead-capture-success {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2rem 1rem;
    gap: 0.75rem;
}

.success-icon {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(16, 185, 129, 0.15);
    color: var(--color-success);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
}

.error-banner {
    padding: 0.625rem 0.875rem;
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid var(--color-error);
    border-radius: var(--radius-sm);
    color: var(--color-error);
    font-size: var(--text-sm);
}

.honeypot {
    position: absolute;
    left: -9999px;
    opacity: 0;
    pointer-events: none;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

@media (max-width: 640px) {
    .form-row {
        grid-template-columns: 1fr;
    }
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
}

.form-label {
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--text-muted);
}

.required {
    color: var(--color-error);
}

.form-input {
    padding: 0.5rem 0.625rem;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-size: var(--text-sm);
    font-family: inherit;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.form-textarea {
    resize: vertical;
    min-height: 80px;
}

.form-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.15);
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 0.5rem;
}
</style>
